import { OfficeMember } from '@/components/OfficeMember';

import { ApPayee } from '@/components/ApPayee';
import { DescriptionCol, DescriptionRow } from '@/components/Description';
import { SuspenseErrorBoundary } from '@/components/SuspenseErrorBoundary';
import { SuspenseLoading } from '@/components/SuspenseErrorBoundary/fallback';
import { useLazyGetApPayeeDetail } from '@/context/services/ap_payee/ApPayee.service';
import { checkIsServerError } from '@/features/errors/utils';
import { useReceiptType } from '@/features/invoices/components/List/Table/Uploader/hooks/useReceiptType';
import { TReceivedInvoiceUpload } from '@/features/invoices/components/List/Table/Uploader/type';
import { RECEIVED_INVOICE_MAX_PIC_COUNT } from '@/features/invoices/constants';
import { BaseOption } from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import {
  FormRadioButton,
  IconApproval,
  ListViewLabel,
  SelectProps,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Control,
  UseFormGetValues,
  UseFormReset,
  useWatch,
} from 'react-hook-form';
import styles from '../index.module.scss';

const cx = classNames.bind(styles);

export type Props = {
  control: Control<TReceivedInvoiceUpload>;
  getValues: UseFormGetValues<TReceivedInvoiceUpload>;
  reset: UseFormReset<TReceivedInvoiceUpload>;
};

export const Right: FC<Props> = memo(({ control, getValues, reset }) => {
  const [showOverwriteMsg, setShowOverwriteMsg] = useState(false);
  const [selectedOfficeMembers, setSelectedOfficeMembers] = useState<
    BaseOption[]
  >([]);
  const { t } = useTranslation();
  const receiptOptions = useReceiptType();
  const apPayeeSelectProps: SelectProps<TReceivedInvoiceUpload> = useMemo(
    () => ({
      mode: 'single',
      maxTagCount: 1,
      className: 'apPayee',
      options: [],
      inlineOptions: true,
    }),
    []
  );

  const receiptTypeRules = useMemo(() => {
    return {
      required: t('required'),
    };
  }, [t]);

  const getApPayeeDetail = useLazyGetApPayeeDetail();
  const picIds = useWatch({ control, name: 'pic_ids' });

  const onAfterPayeeSelect = useCallback(
    async (payeeId: string | number | (string | number)[]) => {
      setShowOverwriteMsg(false);
      if (typeof payeeId === 'string') {
        const data = await getApPayeeDetail(payeeId);
        if (
          data?.contact_office_members &&
          data.contact_office_members.length > 0
        ) {
          reset({
            ...getValues(),
            pic_ids: data?.contact_office_members
              ?.map((member) => member.id)
              .filter((id): id is string => !!id),
          });
          setSelectedOfficeMembers(
            data?.contact_office_members?.map((item) => ({
              label: item.name ?? '',
              value: item.id ?? '',
            })) ?? []
          );
          setShowOverwriteMsg(true);
        }
      }
    },
    [getApPayeeDetail, reset, getValues, setShowOverwriteMsg]
  );

  const onAfterPicIdsSelect = useCallback(() => {
    setShowOverwriteMsg(false);
  }, [setShowOverwriteMsg]);

  useEffect(() => {
    if (picIds.length === 0) {
      setShowOverwriteMsg(false);
    }
  }, [picIds]);

  return (
    <div className={cx(styles['file-info-container'])}>
      <div className={cx(styles['sub-title'])}>{t('e_doc_information')}</div>
      <DescriptionRow>
        <DescriptionCol
          className='table_label'
          label={
            <ListViewLabel>
              <div className={cx(styles['label'])}>
                {t('received_invoice_receipt_type')}
              </div>
            </ListViewLabel>
          }
          labelWidth='140px'
        />
        <DescriptionCol
          value={
            <FormRadioButton<TReceivedInvoiceUpload>
              name='receipt_type'
              control={control}
              inputId='receipt-type'
              options={receiptOptions}
              rules={receiptTypeRules}
              testId='receiptType'
            />
          }
        />
      </DescriptionRow>
      <DescriptionRow>
        <DescriptionCol
          className='table_label'
          label={
            <ListViewLabel>
              <div className={cx(styles['label'])}>
                {t('received_invoice_payee')}
              </div>
            </ListViewLabel>
          }
          labelWidth='140px'
        />
        <DescriptionCol
          className={cx(styles['ap-payee-container'])}
          value={
            <SuspenseErrorBoundary
              shouldPropagate={checkIsServerError}
              fallback={<SuspenseLoading />}
            >
              <ApPayee
                control={control}
                name='ap_payee_id'
                inputId='select-ap-payee'
                testId='ap-payee-select'
                selectProps={apPayeeSelectProps}
                onChangeAfter={onAfterPayeeSelect}
                include_inactive={false}
                showCode
              />
            </SuspenseErrorBoundary>
          }
        />
      </DescriptionRow>
      <DescriptionRow>
        <DescriptionCol
          className='table_label'
          label={
            <ListViewLabel>
              <div className={cx(styles['label'])}>
                {t('received_invoice_pics')}
              </div>
            </ListViewLabel>
          }
          labelWidth='140px'
        />
        <DescriptionCol
          value={
            <div className={cx(styles['pic-ids-container'])}>
              <OfficeMember<TReceivedInvoiceUpload>
                control={control}
                name='pic_ids'
                inputId='pic_ids'
                testId='pic-ids'
                validateMaxCount={RECEIVED_INVOICE_MAX_PIC_COUNT}
                defaultOptions={selectedOfficeMembers}
                onChangeAfter={onAfterPicIdsSelect}
              />
              {showOverwriteMsg && (
                <div
                  className={cx(styles['pic-ids-msg-container'])}
                  data-testid='pic-ids-msg'
                >
                  <IconApproval />
                  <div>{t('received_invoice.uploader.overwrite.msg')}</div>
                </div>
              )}
            </div>
          }
        />
      </DescriptionRow>
    </div>
  );
});
Right.displayName = 'Right';
